import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover } from 'antd'

import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { UserAttributes } from 'api/administration/participants/types'
import styles from './styles.module.scss'

const prefix = 'configuration.users.enableParticipantsModal'

const Text = ({ children, maxWidth }) => {
  return (
    <div
      style={{
        maxWidth,
      }}
      className="text-truncate"
      aria-label={children}
    >
      <Popover overlayClassName={styles.popover} content={children}>
        {children}
      </Popover>
    </div>
  )
}

export const useUsersColumns = () => {
  const { t } = useTranslation()

  type Keys =
    | keyof UserAttributes
    | `area_${keyof UserAttributes['area']}`
    | 'action'
  type Column<Key extends Keys = Keys> = {
    title: string
    dataIndex?: string
    key: Key
    sorteable?: boolean
    width?: number
    render?: (value: any, data: UserAttributes) => any
    filterable?: boolean
  }

  const columns = useMemo(
    (): Column[] => [
      {
        title: t(`${prefix}.table.headers.name`),
        dataIndex: 'name',
        key: 'name',
        render: (_, { id, avatar, fullName }) => (
          <a href={`/configuration/user/${id}`} target="_blank">
            <UserAvatar
              user={{ fullName, avatar }}
              className={styles.userData}
            />
          </a>
        ),
        sorteable: true,
        width: 250,
      } as Column<'name'>,
      {
        title: t(`${prefix}.table.headers.identifier`),
        dataIndex: 'identifier',
        key: 'identifier',
        sorteable: true,
        hideable: true,
        render: (value) => <Text maxWidth={114}>{value}</Text>,
      } as Column<'identifier'>,
      {
        title: t(`${prefix}.table.headers.position`),
        dataIndex: 'position',
        key: 'position',
        sorteable: true,
        render: (value) => <Text maxWidth={114}>{value}</Text>,
      } as Column<'position'>,
      {
        title: t(`${prefix}.table.headers.area`),
        dataIndex: 'area.name',
        key: 'area_name',
        sorteable: true,
        render: (_, { area }) => <Text maxWidth={114}>{area ? area.name: t('workflows.users.noArea')}</Text>,
      } as Column<'area_name'>,
      {
        title: t(`${prefix}.table.headers.email`),
        dataIndex: 'email',
        key: 'email',
        sorteable: true,
        render: (value) => <Text maxWidth={200}>{value}</Text>,
      } as Column<'email'>,
    ],
    [t]
  )
  return columns
}

export default useUsersColumns
